import { track } from '@vercel/analytics';
import { motion, AnimatePresence } from 'framer-motion';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { postJson, getJson } from '@/lib/apiConfig';
import { loginWithCredentials, extractAuthToken } from '@/services/authSession';
import { resolveSalesforceIds } from '@/services/register';
import { getAccountDataByCNPJ } from '@/services/xanoSalesforce';
import { userFacingError } from '@/utils/translateApiMessage';
import './style.css';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Quando true, não fecha ao clicar fora nem com Escape (cadastro obrigatório no portal). */
  mandatory?: boolean;
  onOpenLogin?: () => void;
  onRegisterSuccess?: () => void;
}

type PersonType = 'PF' | 'PJ';

/** O Xano devolve o critério da senha em inglês. O aviso do formulário e o toast usam o mesmo texto. */
function messageFromRegisterError(error: unknown): string {
  return userFacingError(error, 'Erro ao criar conta');
}

interface RegisterData {
  person_type: PersonType;
  nome: string;
  sobrenome: string;
  telefone: string;
  email: string;
  senha: string;
  cpf: string;
  cnpj: string;
  nomeEmpresa: string;
  id_sf_account?: string | null;
  id_sf_contact?: string | null;
  id_sf_lead?: string | null;
}

const RegisterModal = ({
  isOpen,
  onClose,
  mandatory = false,
  onOpenLogin,
  onRegisterSuccess,
}: RegisterModalProps) => {
  const [registerData, setRegisterData] = useState<RegisterData>({
    person_type: 'PJ',
    nome: '',
    sobrenome: '',
    telefone: '',
    email: '',
    senha: '',
    cpf: '',
    cnpj: '',
    nomeEmpresa: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({
    general: '',
  });

  const handleClose = () => {
    setRegisterData({
      person_type: 'PJ',
      nome: '',
      sobrenome: '',
      telefone: '',
      email: '',
      senha: '',
      cpf: '',
      cnpj: '',
      nomeEmpresa: '',
    });
    setErrors({ general: '' });
    setIsLoading(false);
    onClose();
  };

  // Função para validar email
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Função para validar CPF
  const validateCPF = (cpf: string): boolean => {
    const cleanCPF = cpf.replace(/\D/g, '');

    // Regex para CPF (11 dígitos)
    const cpfRegex = /^\d{11}$/;
    if (!cpfRegex.test(cleanCPF)) return false;

    // Verificar se não são todos os dígitos iguais
    if (/^(\d)\1{10}$/.test(cleanCPF)) return false;

    // Validação do algoritmo do CPF
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleanCPF.charAt(9))) return false;

    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    return remainder === parseInt(cleanCPF.charAt(10));
  };

  // Função para validar CNPJ
  const validateCNPJ = (cnpj: string): boolean => {
    const cleanCNPJ = cnpj.replace(/\D/g, '');

    // Regex para CNPJ (14 dígitos)
    const cnpjRegex = /^\d{14}$/;
    if (!cnpjRegex.test(cleanCNPJ)) return false;

    // Verificar se não são todos os dígitos iguais
    if (/^(\d)\1{13}$/.test(cleanCNPJ)) return false;

    // Validação do algoritmo do CNPJ
    let sum = 0;
    let weight = 2;
    for (let i = 11; i >= 0; i--) {
      sum += parseInt(cleanCNPJ.charAt(i)) * weight;
      weight = weight === 9 ? 2 : weight + 1;
    }
    let remainder = sum % 11;
    const firstDigit = remainder < 2 ? 0 : 11 - remainder;
    if (firstDigit !== parseInt(cleanCNPJ.charAt(12))) return false;

    sum = 0;
    weight = 2;
    for (let i = 12; i >= 0; i--) {
      sum += parseInt(cleanCNPJ.charAt(i)) * weight;
      weight = weight === 9 ? 2 : weight + 1;
    }
    remainder = sum % 11;
    const secondDigit = remainder < 2 ? 0 : 11 - remainder;
    return secondDigit === parseInt(cleanCNPJ.charAt(13));
  };

  // Função para validar telefone
  // Mínimo: 2 dígitos DDD + 8 dígitos telefone = 10 dígitos
  // Máximo: 2 dígitos DDD + 9 dígitos telefone = 11 dígitos
  const validatePhone = (phone: string): boolean => {
    const cleanPhone = phone.replace(/\D/g, '');
    
    // Telefone deve ter entre 10 e 11 dígitos (DDD + telefone)
    if (cleanPhone.length < 10 || cleanPhone.length > 11) {
      return false;
    }
    
    // DDD deve ter pelo menos 2 dígitos (primeiros 2 dígitos)
    const ddd = cleanPhone.substring(0, 2);
    if (ddd.length < 2) {
      return false;
    }
    
    // Telefone deve ter pelo menos 8 dígitos (após o DDD)
    const telefone = cleanPhone.substring(2);
    if (telefone.length < 8) {
      return false;
    }
    
    return true;
  };

  // Função para validar campos
  const validateFields = (): boolean => {
    if (!registerData.email.trim()) {
      setErrors({ general: 'Email é obrigatório' });
      return false;
    }
    if (!validateEmail(registerData.email)) {
      setErrors({ general: 'Email inválido' });
      return false;
    }
    if (!registerData.senha.trim()) {
      setErrors({ general: 'Senha é obrigatória' });
      return false;
    }
    if (registerData.senha.length < 8) {
      setErrors({ general: 'A senha deve ter pelo menos 8 caracteres.' });
      return false;
    }
    if (registerData.person_type === 'PF') {
      if (!registerData.nome.trim()) {
        setErrors({ general: 'Nome é obrigatório' });
        return false;
      }
      if (!registerData.sobrenome.trim()) {
        setErrors({ general: 'Sobrenome é obrigatório' });
        return false;
      }
      if (!registerData.cpf.trim()) {
        setErrors({ general: 'CPF é obrigatório para pessoa física' });
        return false;
      }
      if (!validateCPF(registerData.cpf)) {
        setErrors({ general: 'CPF inválido' });
        return false;
      }
    } else {
      if (!registerData.nomeEmpresa.trim()) {
        setErrors({ general: 'Nome da empresa é obrigatório' });
        return false;
      }
      if (!registerData.cnpj.trim()) {
        setErrors({ general: 'CNPJ é obrigatório para pessoa jurídica' });
        return false;
      }
      if (!validateCNPJ(registerData.cnpj)) {
        setErrors({ general: 'CNPJ inválido' });
        return false;
      }
      if (!registerData.nome.trim()) {
        setErrors({ general: 'Nome do responsável é obrigatório' });
        return false;
      }
    }
    if (!registerData.telefone.trim()) {
      setErrors({ general: 'Telefone é obrigatório' });
      return false;
    }
    if (!validatePhone(registerData.telefone)) {
      setErrors({ general: 'Telefone inválido. Digite o DDD e o número (mínimo 10 dígitos)' });
      return false;
    }
    return true;
  };

  // Função para aplicar máscara de CPF
  const formatCPF = (value: string): string => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length <= 11) {
      return cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
  };

  // Função para aplicar máscara de CNPJ
  const formatCNPJ = (value: string): string => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length <= 14) {
      return cleanValue.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        '$1.$2.$3/$4-$5'
      );
    }
    return value;
  };

  // Função para remover apenas dígitos
  const onlyDigits = (s: string): string => s.replace(/\D/g, '');

  // Função para formatar telefone (apenas números, sem máscara)
  const formatPhone = (value: string): string => {
    // Remove tudo que não é número
    return value.replace(/\D/g, '');
  };

  // Função para fazer registro na API
  const registerUser = async (data: RegisterData) => {
    // Normalizar dados antes do envio
    const nomeResp = (data.nome || '').trim();
    const firstSpace = nomeResp.indexOf(' ');
    const firstNamePJ = firstSpace > 0 ? nomeResp.slice(0, firstSpace).trim() : nomeResp;
    const lastNamePJ = firstSpace > 0 ? nomeResp.slice(firstSpace + 1).trim() : '';

    const payload = {
      person_type: data.person_type,
      email: data.email.trim(),
      password: data.senha,
      first_name: data.person_type === 'PJ' ? firstNamePJ : data.nome,
      last_name: data.person_type === 'PF' ? (data.sobrenome || '').trim() : lastNamePJ,
      phone: onlyDigits(data.telefone), // Envia apenas números
      company_name: data.person_type === 'PJ' ? (data.nomeEmpresa || '').trim() : '',
      // redundâncias para compatibilidade com possíveis mapeamentos do backend
      companyName: data.person_type === 'PJ' ? (data.nomeEmpresa || '').trim() : undefined,
      company: data.person_type === 'PJ' ? (data.nomeEmpresa || '').trim() : undefined,
      cpf: data.person_type === 'PF' ? onlyDigits(data.cpf) : null,
      cnpj: data.person_type === 'PJ' ? onlyDigits(data.cnpj) : null,
      id_sf_account: data.id_sf_account || null,
      id_sf_contact: data.id_sf_contact || null,
      id_sf_lead: data.id_sf_lead || null,
    };

    const result = await postJson<any>('9-ms6pAp', 'auth/signup', payload);
    return result;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Limpa erros anteriores
    setErrors({ general: '' });

    // Valida campos
    if (!validateFields()) {
      return;
    }

    setIsLoading(true);

    try {
      const salesforcePayload = {
        isCompany: registerData.person_type === 'PJ',
        cnpj:
          registerData.person_type === 'PJ'
            ? onlyDigits(registerData.cnpj)
            : undefined,
        email: registerData.email,
        firstName: registerData.nome,
        lastName:
          registerData.person_type === 'PF'
            ? registerData.sobrenome
            : registerData.nomeEmpresa,
        phone: onlyDigits(registerData.telefone),
        mobile: onlyDigits(registerData.telefone),
      };

      // 1) Cadastro sem IDs Salesforce — preenchidos após login (rotas sf/* exigem Bearer)
      const registerPayload: RegisterData = {
        ...registerData,
        id_sf_account: null,
        id_sf_contact: null,
        id_sf_lead: null,
      };

      const signupResponse = await registerUser(registerPayload);

      // 2) Login após signup: JWT alinhado ao consumo das rotas sf/* (prioridade sobre authToken eventual do signup)
      let authToken: string | null = extractAuthToken(signupResponse);
      try {
        const loginResponse = await loginWithCredentials(
          registerData.email,
          registerData.senha
        );
        authToken = extractAuthToken(loginResponse) ?? authToken;
      } catch {
        // mantém token do signup se login falhar
      }
      if (!authToken) {
        authToken = extractAuthToken(signupResponse);
      }

      if (authToken) {
        localStorage.setItem('authToken', authToken);
        localStorage.setItem('userEmail', registerData.email);

        try {
          const me = await getJson<any>('9-ms6pAp', 'auth/me');

          if (me?.id) {
            localStorage.setItem('user_id', String(me.id));
          }

          try {
            await resolveSalesforceIds(salesforcePayload);
          } catch {
            // Salesforce não bloqueia conclusão do cadastro
          }

          const personType =
            me?.person_type || registerData.person_type || 'PF';

          if (personType === 'PJ') {
            const possibleCNPJ =
              me?.cnpj ||
              me?.CNPJ ||
              me?.company_document ||
              me?.tax_id ||
              me?.document ||
              '';
            const cnpj =
              onlyDigits(possibleCNPJ) || onlyDigits(registerData.cnpj);

            let wallet = 'GOLD';
            if (cnpj && cnpj.length >= 14) {
              try {
                const acc = await getAccountDataByCNPJ(cnpj);
                const tier = (
                  acc?.CE_Carteira_c || acc?.ce_carteira_c || ''
                )
                  .toString()
                  .trim();
                wallet = tier ? tier.toUpperCase() : 'GOLD';
              } catch {
                // 404/erro: mantém GOLD
              }
            }
            localStorage.setItem('wallet', wallet);
          } else {
            localStorage.setItem('wallet', 'GOLD');
          }
        } catch {
          localStorage.setItem('wallet', 'GOLD');
        }

        window.dispatchEvent(new CustomEvent('user-logged-in'));
      }

      // Toast de sucesso
      const successToastId = toast.success('Conta criada com sucesso!', {
        position: 'top-right',
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });

      track('Registro Realizado', {
        email: registerData.email,
      });

      // Fallback: forçar fechamento após 2.5s se autoClose não funcionar
      setTimeout(() => {
        toast.dismiss(successToastId);
      }, 2500);

      // Chama callback de sucesso se fornecida (após login automático)
      if (onRegisterSuccess) {
        onRegisterSuccess();
      }

      // Fecha o modal após sucesso
      handleClose();
    } catch (error) {
      const errorMessage = messageFromRegisterError(error);

      // Define erro geral
      setErrors({ general: errorMessage });

      // Toast de erro
      const errorToastId = toast.error(errorMessage, {
        autoClose: 3000,
        closeOnClick: true,
      });

      // Fallback: forçar fechamento após 3.5s se autoClose não funcionar
      setTimeout(() => {
        toast.dismiss(errorToastId);
      }, 3500);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRegisterData(prev => ({
      ...prev,
      [name]: value,
    }));

    // Limpa erro geral quando usuário digita
    if (errors.general) {
      setErrors({ general: '' });
    }
  };

  useEffect(() => {
    if (!mandatory || !isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [mandatory, isOpen]);

  const handlePersonTypeChange = (type: PersonType) => {
    setRegisterData(prev => ({
      ...prev,
      person_type: type,
      // Limpa campos específicos ao trocar tipo
      cpf: '',
      cnpj: '',
      nomeEmpresa: '',
      sobrenome: '',
    }));
  };

  const handleCPFChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCPF(e.target.value);
    setRegisterData(prev => ({
      ...prev,
      cpf: formatted,
    }));
  };

  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatCNPJ(e.target.value);
    setRegisterData(prev => ({
      ...prev,
      cnpj: formatted,
    }));
  };

  // Handler específico para telefone - aceita apenas números
  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhone(e.target.value);
    // Limita a 11 dígitos (2 DDD + 9 telefone)
    const limited = formatted.slice(0, 11);
    setRegisterData(prev => ({
      ...prev,
      telefone: limited,
    }));

    // Limpa erro geral quando usuário digita
    if (errors.general) {
      setErrors({ general: '' });
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={`register-modal-overlay${mandatory ? ' register-modal-overlay--mandatory' : ''}`}
          onClick={undefined}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <motion.div
            className='register-modal-container'
            onClick={e => e.stopPropagation()}
            initial={{ opacity: 0, y: -30, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -30, scale: 0.9 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
          >
            <motion.div
              className='register-modal-header'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut', delay: 0.1 }}
            >
              <h2 className='register-modal-title'>Criar Conta</h2>
              <p className='register-modal-subtitle'>
                Preencha seus dados para começar
              </p>
            </motion.div>

            <motion.form
              onSubmit={handleSubmit}
              className='register-modal-form'
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut', delay: 0.2 }}
            >
              {/* Toggle Pessoa Física / Jurídica */}
              <motion.div
                className='register-form-group'
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut', delay: 0.3 }}
              >
                <label className='register-form-label'>Tipo de Pessoa</label>
                <div className='register-toggle-container'>
                  <button
                    type='button'
                    onClick={() => handlePersonTypeChange('PF')}
                    className={`register-toggle-btn ${registerData.person_type === 'PF' ? 'active' : ''}`}
                  >
                    Pessoa Física
                  </button>
                  <button
                    type='button'
                    onClick={() => handlePersonTypeChange('PJ')}
                    className={`register-toggle-btn ${registerData.person_type === 'PJ' ? 'active' : ''}`}
                  >
                    Pessoa Jurídica
                  </button>
                </div>
              </motion.div>

              {/* Campos dinâmicos baseados no tipo */}
              {registerData.person_type === 'PF' ? (
                <>
                  <motion.div
                    className='register-form-row'
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut', delay: 0.4 }}
                  >
                    <div className='register-form-group'>
                      <label htmlFor='nome' className='register-form-label'>
                        Nome
                      </label>
                      <input
                        type='text'
                        id='nome'
                        name='nome'
                        value={registerData.nome}
                        onChange={handleInputChange}
                        className='register-form-input'
                        placeholder='Seu nome'
                        required
                      />
                    </div>
                    <div className='register-form-group'>
                      <label
                        htmlFor='sobrenome'
                        className='register-form-label'
                      >
                        Sobrenome
                      </label>
                      <input
                        type='text'
                        id='sobrenome'
                        name='sobrenome'
                        value={registerData.sobrenome}
                        onChange={handleInputChange}
                        className='register-form-input'
                        placeholder='Seu sobrenome'
                        required
                      />
                    </div>
                  </motion.div>

                  {/* Campo CPF para Pessoa Física */}
                  <motion.div
                    className='register-form-group'
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut', delay: 0.5 }}
                  >
                    <label htmlFor='cpf' className='register-form-label'>
                      CPF
                    </label>
                    <input
                      type='text'
                      id='cpf'
                      name='cpf'
                      value={registerData.cpf}
                      onChange={handleCPFChange}
                      className='register-form-input'
                      placeholder='000.000.000-00'
                      maxLength={14}
                      required
                    />
                  </motion.div>
                </>
              ) : (
                <>
                  <motion.div
                    className='register-form-group'
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut', delay: 0.4 }}
                  >
                    <label
                      htmlFor='nomeEmpresa'
                      className='register-form-label'
                    >
                      Nome da Empresa
                    </label>
                    <input
                      type='text'
                      id='nomeEmpresa'
                      name='nomeEmpresa'
                      value={registerData.nomeEmpresa}
                      onChange={handleInputChange}
                      className='register-form-input'
                      placeholder='Nome da sua empresa'
                      required
                    />
                  </motion.div>
                  <motion.div
                    className='register-form-group'
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut', delay: 0.5 }}
                  >
                    <label htmlFor='cnpj' className='register-form-label'>
                      CNPJ
                    </label>
                    <input
                      type='text'
                      id='cnpj'
                      name='cnpj'
                      value={registerData.cnpj}
                      onChange={handleCNPJChange}
                      className='register-form-input'
                      placeholder='00.000.000/0000-00'
                      maxLength={18}
                      required
                    />
                  </motion.div>
                  <motion.div
                    className='register-form-group'
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut', delay: 0.6 }}
                  >
                    <label htmlFor='nome' className='register-form-label'>
                      Nome do Responsável
                    </label>
                    <input
                      type='text'
                      id='nome'
                      name='nome'
                      value={registerData.nome}
                      onChange={handleInputChange}
                      className='register-form-input'
                      placeholder='Nome do responsável'
                      required
                    />
                  </motion.div>
                </>
              )}

              {/* Campos Comuns */}
              <motion.div
                className='register-form-group'
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut', delay: 0.7 }}
              >
                <label htmlFor='telefone' className='register-form-label'>
                  Telefone
                </label>
                <input
                  type='tel'
                  id='telefone'
                  name='telefone'
                  value={registerData.telefone}
                  onChange={handlePhoneChange}
                  className='register-form-input'
                  placeholder='11982246893'
                  inputMode='numeric'
                  pattern='[0-9]*'
                  maxLength={11}
                  required
                />
              </motion.div>

              <motion.div
                className='register-form-group'
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut', delay: 0.8 }}
              >
                <label htmlFor='email' className='register-form-label'>
                  Email
                </label>
                <input
                  type='email'
                  id='email'
                  name='email'
                  value={registerData.email}
                  onChange={handleInputChange}
                  className='register-form-input'
                  placeholder='seu@email.com'
                  required
                />
              </motion.div>

              <motion.div
                className='register-form-group'
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: 'easeOut', delay: 0.9 }}
              >
                <label htmlFor='senha' className='register-form-label'>
                  Senha
                </label>
                <input
                  type='password'
                  id='senha'
                  name='senha'
                  value={registerData.senha}
                  onChange={handleInputChange}
                  className='register-form-input'
                  placeholder='Sua senha'
                  required
                />
              </motion.div>

              {errors.general && (
                <motion.div
                  className='register-form-error-general'
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  {errors.general}
                </motion.div>
              )}

              <motion.div
                className='register-form-actions'
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.25, ease: 'easeOut', delay: 1.0 }}
              >
                <button
                  type='submit'
                  className={`register-btn-primary ${isLoading ? 'register-btn-primary--loading' : ''}`}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <div className='register-spinner'></div>
                      Criando conta...
                    </>
                  ) : (
                    'Criar Conta'
                  )}
                </button>
              </motion.div>
            </motion.form>

            <motion.div
              className='register-modal-footer'
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.25, ease: 'easeOut', delay: 1.1 }}
            >
              <p className='register-login-text'>
                Já tem uma conta?{' '}
                <button
                  type='button'
                  onClick={onOpenLogin}
                  className='register-login-link'
                >
                  Entre aqui
                </button>
              </p>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RegisterModal;
